import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Toolbar,
  makeStyles,
  Typography,
  Link,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  IconButton
} from '@material-ui/core';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useDispatch, useSelector } from 'react-redux';
import { accountActions } from '../slices/accountSlice';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';
import UserFilter from './filter/UserFilter';
import ContrastIcon from '@mui/icons-material/Contrast';
import { themeActions } from '../slices/themeSlice';
import { THEMES } from '../constants';

const useStyles = makeStyles(theme => ({
  doneLoading: {
    display: 'none'
  },
  hidden: {
    display: 'none'
  },
  mainContainer: {
    background: theme.palette.background.dark,
    color: theme.palette.text.secondary,
    height: 'fit-content',
    minHeight: '100vh',
    width: '100%',
    minWidth: '1200px',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column'
  },
  navContainer: {
    paddingLeft: '2rem',
    paddingRight: '2rem',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    backgroundColor: theme.palette.background.dark,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.text.primary}40`
  },
  hamburger: {
    position: 'absolute',
    right: '-1rem'
  },
  desktopNav: {
    marginLeft: '2rem',
    width: 'fit-content',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center'
  },
  secondaryLink: {
    color: 'white',
    textAlign: 'center'
  },
  mobileDrawer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.dark,
    borderRight: `0.1rem solid ${theme.palette.secondary.main}`,
    overflow: 'hidden'
  },
  mobileNavEl: {
    marginTop: '1rem',
    marginBottom: '1rem',
    marginLeft: '1rem',
    marginRight: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%'
  },
  mobileDivider: {
    backgroundColor: 'white',
    marginLeft: '1rem',
    marginRight: '1rem'
  },
  footerSection: {
    color: 'white',
    width: '100%',
    height: '100px',
    paddingLeft: '2rem',
    paddingRight: '2rem',
    marginTop: '150px',
    display: 'flex',
    justifyContent: 'space-evenly',
    zIndex: 2
  },
  footerAbout: {
    alignSelf: 'center',
    paddingLeft: '1rem',
    display: 'flex',
    width: '20rem',
    textAlign: 'left',
    flex: 1,
    height: ''
  },
  footerLink: {
    alignSelf: 'flex-start',
    backgroundColor: theme.palette.background.dark,
    width: 'fit-content',
    transform: 'translate(0, -50%)',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flexWrap: 'nowrap'
  },
  footerCopyright: {
    alignSelf: 'center',
    width: '20rem',
    textAlign: 'right',
    paddingRight: '1rem',
    flex: 1
  },
  discordLink: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    paddingLeft: '7rem',
    paddingRight: '7rem'
  },
  discordText: {
    marginTop: '1rem'
  },
  githubIcon: {
    marginRight: '1rem'
  },
  link: {
    '&:hover': {
      color: theme.palette.text.secondary
    }
  },
  mainLink: {
    '&:hover': {
      color: theme.palette.text.secondary
    }
  },
  navBar: {
    justifyContent: 'space-between'
  },
  mainNavSection: {
    display: 'flex',
    alignItems: 'center'
  },
  buttonNavSection: {
    display: 'flex',
    alignItems: 'center'
  },
  dropdown: {
    backgroundColor: 'transparent',
    color: 'white',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.text.secondary
    },
    width: '12rem'
  },
  dropdownItem: {
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  themeMode: {
    backgroundColor: 'transparent',
    display: 'flex',
    marginLeft: '1rem',
    marginRight: '1rem',
    '&.MuiButtonBase-root': {
      color: 'white'
    }
  },
  menu: {
    backgroundColor: theme.palette.background.dark,
    width: 'fit-content'
  },
  menuItem: {
    backgroundColor: theme.palette.background.dark
  },
  profileInfo: {
    display: 'flex',
    width: 'fit-content',
    height: '100%',
    flexWrap: 'nowrap',
    alignItems: 'center'
  },
  searchIcon: {
    padding: theme.spacing(0, 2),
    color: theme.palette.tertiary.secondary,
    height: '100%',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  search: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.secondary.secondary,
    marginRight: 0,
    marginLeft: '10rem',
    width: '100%',
    height: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(5),
      width: 'auto'
    }
  }
}));

function Page(props) {
  const classes = useStyles();
  const theme = useTheme();
  const account = useSelector(state => state.account);
  const history = useHistory();
  const dispatch = useDispatch();
  const isLarge = useMediaQuery(theme.breakpoints.up('1700'));
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const changeTheme = () => {
    dispatch(
      themeActions.setTheme(
        localStorage.getItem('theme') === THEMES.DARK
          ? THEMES.LIGHT
          : THEMES.DARK
      )
    );
  };

  const themeMode = useSelector(state => state.theme.theme);

  const styles = {
    input: (provided, state) => ({
      ...provided,
      color: theme.palette.tertiary.secondary
    }),

    option: (provided, state) => ({
      ...provided,
      color: state.isFocused ? '#fff' : theme.palette.tertiary.secondary,
      backgroundColor: state.isFocused
        ? theme.palette.background.paperbg
        : theme.palette.secondary.secondary,
      paddingTop: '0.5rem',
      paddingBottom: '0.5rem'
    }),

    menu: (provided, state) => ({
      ...provided,
      border: state.isFocused
        ? theme.palette.selected.active
        : '1px solid #ced3d3',
      borderRadius: 5,
      backgroundColor: theme.palette.secondary.secondary,
      width: isLarge ? '30rem' : '18rem',
      fontSize: '16px',
      fontFamily: 'Inter',
      padding: '2px'
    }),

    control: (_, { selectProps: { width } }) => ({
      width: isLarge ? '30rem' : '18rem',
      height: '3rem',
      fontSize: '16px',
      fontFamily: 'Inter',
      backgroundColor: theme.palette.secondary.secondary,
      display: 'flex',
      flexWrap: 'nowrap',
      cursor: 'text'
    }),

    singleValue: (provided, state) => ({
      ...provided,
      color: theme.palette.tertiary.secondary
    }),

    menuPortal: provided => ({ ...provided, zIndex: 9999 })
  };

  function handleProfileOpen(event) {
    setAnchorEl(event.currentTarget);
    setProfileMenuOpen(true);
  }

  function handleProfileClose(event) {
    setAnchorEl(event.currentTarget);
    setProfileMenuOpen(false);
  }

  async function handleLogout() {
    try {
      props.history.push('/');
      await dispatch(accountActions.logout());
    } catch (error) {
      enqueueSnackbar('Unable to logout', {
        variant: 'error'
      });
    }
  }

  function handleLogin() {
    history.push('/login');
  }

  function handleSignUp() {
    history.push('/register');
  }

  function handleSearch(select) {
    setSearchValue(null);
    history.push(`/public-profile?name=${select.label}`);
  }

  function DPSDropDown() {
    const [dpsAnchor, setDpsAnchor] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    return (
      <>
        <Button
          variant="contained"
          disableElevation
          aria-haspopup="true"
          aria-expanded={profileMenuOpen ? 'true' : undefined}
          onClick={event => {
            setDropdownOpen(true);
            setDpsAnchor(event.currentTarget);
          }}
          className={classes.dropdown}
          style={{ width: '100%', justifyContent: 'flex-end' }}
        >
          <Typography
            style={{ fontSize: '16px' }}
            variant="h3"
            component="div"
            align="center"
          >
            DPS
          </Typography>
        </Button>
        <Menu
          anchorEl={dpsAnchor}
          open={dropdownOpen}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
          getContentAnchorEl={null}
          onClose={() => setDropdownOpen(false)}
          classes={{ paper: classes.menu }}
        >
          <Link
            className={clsx(classes.secondaryLink, classes.link)}
            href="/dps/overworld"
            underline="none"
          >
            <MenuItem className={classes.dropdownItem}>
              <Typography
                style={{ fontSize: '14px' }}
                variant="h3"
                component="div"
              >
                Standard
              </Typography>
            </MenuItem>
          </Link>
          <Link
            className={clsx(classes.secondaryLink, classes.link)}
            href="/restricted-dps/weekly-boss"
            underline="none"
          >
            <MenuItem className={classes.dropdownItem}>
              <Typography
                style={{ fontSize: '14px' }}
                variant="h3"
                component="div"
              >
                Restricted
              </Typography>
            </MenuItem>
          </Link>
        </Menu>
      </>
    );
  }

  return (
    <Box className={classes.mainContainer}>
      {props.helmet}
      <AppBar
        position="static"
        className={classes.navContainer}
        id="golden-house-appbar"
      >
        <Box>
          <Toolbar disableGutters className={classes.navBar}>
            <Box className={classes.mainNavSection}>
              <Link href="/" underline="none">
                <Typography
                  variant="h1"
                  noWrap
                  component="div"
                  sx={{ mr: 2, display: { xs: 'none', md: 'flex' } }}
                  className={classes.mainLink}
                  style={{ fontSize: '24px' }}
                >
                  The Golden House
                </Typography>
              </Link>
              <Box className={classes.desktopNav}>
                <Link
                  className={clsx(classes.secondaryLink, classes.link)}
                  href="/speedrun/abyss/leaderboard"
                  underline="none"
                >
                  <Box className={classes.desktopNav}>
                    <Typography
                      style={{ fontSize: '16px' }}
                      variant="h3"
                      component="div"
                      align="center"
                    >
                      Speedrunning
                    </Typography>
                  </Box>
                </Link>
              </Box>

              <Box className={classes.desktopNav}>
                <DPSDropDown />
              </Box>
              <Link
                className={clsx(classes.secondaryLink, classes.link)}
                href="/leaderboards/abyss"
                underline="none"
              >
                <Box
                  className={classes.desktopNav}
                  style={{ marginLeft: '2rem' }}
                >
                  <Typography
                    style={{ fontSize: '16px' }}
                    variant="h3"
                    component="div"
                    align="center"
                  >
                    Abyss Standings
                  </Typography>
                </Box>
              </Link>
              <div className={classes.search}>
                <div className={classes.searchIcon}>
                  <SearchIcon />
                </div>
                <UserFilter
                  styles={styles}
                  isMulti={false}
                  components={{
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null
                  }}
                  placeholder={'Search for a player...'}
                  isClearable={false}
                  value={searchValue}
                  onChange={handleSearch}
                />
              </div>
            </Box>
            <IconButton
              id="change-theme"
              onClick={changeTheme}
              classes={{
                root: classes.themeMode
              }}
            >
              <ContrastIcon
                style={{
                  transform: `rotate(${themeMode === 'DARK' ? 180 : 0}deg)`,
                  transition: 'transform 0.3s ease'
                }}
              />
            </IconButton>
            {account.isLoggedIn && (
              <Box className={classes.profileInfo}>
                <Button
                  variant="contained"
                  disableElevation
                  aria-haspopup="true"
                  aria-expanded={profileMenuOpen ? 'true' : undefined}
                  style={{ justifyContent: 'flex-end' }}
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={handleProfileOpen}
                  className={classes.dropdown}
                >
                  <Typography
                    style={{
                      fontSize: '16px',
                      color: theme.palette.secondary.main
                    }}
                    variant="h4"
                  >
                    {account.currentUser.name}
                  </Typography>
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={profileMenuOpen}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                  }}
                  getContentAnchorEl={null}
                  onClose={handleProfileClose}
                  classes={{ paper: classes.menu }}
                >
                  <Link
                    className={clsx(classes.secondaryLink, classes.link)}
                    href="/private/user-profile"
                    underline="none"
                  >
                    <MenuItem className={classes.dropdownItem}>
                      <Typography
                        style={{ fontSize: '14px' }}
                        variant="h4"
                        component="div"
                      >
                        Profile
                      </Typography>
                    </MenuItem>
                  </Link>
                  <Link
                    className={clsx(classes.secondaryLink, classes.link)}
                    href="/private/submit-run"
                    underline="none"
                  >
                    <MenuItem className={classes.dropdownItem}>
                      <Typography
                        style={{ fontSize: '14px' }}
                        variant="h4"
                        component="div"
                      >
                        Submit a Speedrun
                      </Typography>
                    </MenuItem>
                  </Link>
                  <Link
                    className={clsx(classes.secondaryLink, classes.link)}
                    href="/private/submit-dps"
                    underline="none"
                  >
                    <MenuItem className={classes.dropdownItem}>
                      <Typography
                        style={{ fontSize: '14px' }}
                        variant="h4"
                        component="div"
                      >
                        Submit a Standard DPS Entry
                      </Typography>
                    </MenuItem>
                  </Link>
                  <Link
                    className={clsx(classes.secondaryLink, classes.link)}
                    href="/private/submit-restricted-dps"
                    underline="none"
                  >
                    <MenuItem className={classes.dropdownItem}>
                      <Typography
                        style={{ fontSize: '14px' }}
                        variant="h4"
                        component="div"
                      >
                        Submit a Restricted DPS Entry
                      </Typography>
                    </MenuItem>
                  </Link>
                  {account.currentUser.role === 'AGENT' && (
                    <Link
                      className={clsx(classes.secondaryLink, classes.link)}
                      href="/private/agent/dashboard"
                      underline="none"
                    >
                      <MenuItem className={classes.dropdownItem}>
                        <Typography
                          variant="h4"
                          component="div"
                          style={{ fontSize: '14px' }}
                        >
                          Agent Dashboard
                        </Typography>
                      </MenuItem>
                    </Link>
                  )}
                  {account.currentUser.role === 'ADMIN' && (
                    <Link
                      className={clsx(classes.secondaryLink, classes.link)}
                      href="/private/admin/dashboard"
                      underline="none"
                    >
                      <MenuItem className={classes.dropdownItem}>
                        <Typography
                          style={{ fontSize: '14px' }}
                          variant="h4"
                          component="div"
                        >
                          Admin Dashboard
                        </Typography>
                      </MenuItem>
                    </Link>
                  )}
                  <Link
                    className={clsx(classes.secondaryLink, classes.link)}
                    onClick={handleLogout}
                    underline="none"
                  >
                    <MenuItem className={classes.dropdownItem}>
                      <Typography
                        variant="h4"
                        component="div"
                        style={{ fontSize: '14px' }}
                      >
                        Logout
                      </Typography>
                    </MenuItem>
                  </Link>
                </Menu>
              </Box>
            )}
            {!account.isLoggedIn && (
              <Box style={{ display: 'flex', flexWrap: 'nowrap' }}>
                <Button onClick={handleLogin}>
                  <Typography
                    style={{
                      fontSize: '16px',
                      textTransform: 'none',
                      color: theme.palette.primary.main
                    }}
                    variant="h4"
                    component="div"
                  >
                    Login
                  </Typography>
                </Button>
                <Button
                  className={classes.logoutBtn}
                  style={{ fontSize: '16px' }}
                  onClick={handleSignUp}
                >
                  <Typography
                    style={{
                      fontSize: '16px',
                      textTransform: 'none',
                      color: theme.palette.primary.main
                    }}
                    variant="h4"
                    component="div"
                  >
                    Register
                  </Typography>
                </Button>
              </Box>
            )}
          </Toolbar>
        </Box>
      </AppBar>
      {props.children}
      <Box className={classes.footerSection}>
        <Typography variant="h6">
          © 2022 BY THE GOLDEN HOUSE. THE GOLDEN HOUSE is not affiliated with
          miHoYo. Genshin Impact, game content and materials are trademarks and
          copyrights of miHoYo.
        </Typography>
      </Box>
    </Box>
  );
}

Page.propTypes = {
  helmet: PropTypes.object.isRequired
};

export default Page;
